import React, { useEffect } from "react";
import { Container, CssBaseline, Box, Avatar, Typography } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
  getAuthLoading,
  login,
  getAuthStatus,
  getAuthError,
  updateStatus,
  logOut,
  selectAuthUser,
} from "../../features/authSlice";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import TextFieldCompnent from "../../components/textFieldComponent";
import ButtonComponent from "../../components/ButtonComponent";
import { UserRole } from "../../models/user.model";
import { useRestaurant } from "../../hooks/RestaurantContext";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../components/LanguageSwitcher";
const SignIn = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userStatus = useSelector(getAuthStatus);
  const error = useSelector(getAuthError);
  const loading = useSelector(getAuthLoading);
  const authUser = useSelector(selectAuthUser);
  const { setRestaurantId, setRestaurantName, setRestaurantLogo, setCurrency } = useRestaurant();

  const schema = yup.object().shape({
    password: yup.string().required(t("auth.required")),
    email: yup
      .string()
      .email(t("auth.emailInvalid"))
      .required(t("auth.required")),
  });

  const initialValues = {
    email: "",
    password: "",
  };

  useEffect(() => {
    if (userStatus === "idle") return;
    if (userStatus === "logedIn") {
      const user = authUser;
      if (user && ![UserRole.admin, UserRole.manager].includes(user.role)) {
        toast.error(t("auth.unauthorized"));
        dispatch(logOut());
      } else if (user) {
        toast.success(t("auth.loginSuccess"));
        if (user.role === UserRole.admin) {
          navigate("/restaurants");
        } else if (user.role === UserRole.manager && user.restaurants.length > 1) {
          navigate("/restaurants");
        } else {
          setRestaurantId(user.restaurants[0].restaurantId ?? "");
          setRestaurantName(user.restaurants[0].name ?? "");
          setRestaurantLogo(user.restaurants[0].logo ?? "");
          setCurrency(user.restaurants[0].currency ?? "");
          navigate("/dashboard");
        }
      }
      dispatch(updateStatus());
    } else if (userStatus === "error") {
      toast.error(error);
    }
  }, [userStatus, error, authUser, navigate, dispatch, setRestaurantId, setRestaurantName, setRestaurantLogo, setCurrency, t]);

  const handleLogin = (values) => {
    const { email, password } = values;
    dispatch(
      login({
        email: email,
        password: password,
      })
    );
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{ height: "100dvh", display: "grid", placeItems: "center", position: "relative" }}
    >
      <CssBaseline />
      <Box sx={{ position: "fixed", top: 16, insetInlineEnd: 16, zIndex: 2 }}>
        <LanguageSwitcher color="#fff" />
      </Box>
      <Box
        sx={{
          backgroundColor: "rgba(18, 22, 30, 0.88)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 3,
          boxShadow: "none",
          padding: 4,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5" sx={{ color: "#fff", mb: 2 }}>
            {t("auth.signIn")}
          </Typography>
          <Formik
            onSubmit={handleLogin}
            initialValues={initialValues}
            validationSchema={schema}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              handleSubmit,
            }) => (
              <form onSubmit={handleSubmit}>
                <TextFieldCompnent
                  variant="outlined"
                  fullWidth
                  type="email"
                  label={t("auth.email")}
                  change={handleChange}
                  value={values.email}
                  name="email"
                  blur={handleBlur}
                  touched={touched.email}
                  error={errors.email}
                  required
                  sx={{
                    input: { color: "#fff" },
                    mb: 4,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#fff",
                      },
                      "&:hover fieldset": {
                        borderColor: "#fff",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#fff",
                      },
                    },
                  }}
                  InputLabelProps={{ style: { color: "#fff" } }}
                />
                <TextFieldCompnent
                  variant="outlined"
                  fullWidth
                  type="password"
                  label={t("auth.password")}
                  change={handleChange}
                  value={values.password}
                  name="password"
                  blur={handleBlur}
                  touched={touched.password}
                  error={errors.password}
                  required
                  sx={{
                    input: { color: "#fff" },
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#fff",
                      },
                      "&:hover fieldset": {
                        borderColor: "#fff",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#fff",
                      },
                    },
                  }}
                  InputLabelProps={{ style: { color: "#fff" } }}
                />
                <Box display="flex" justifyContent="center">
                  <ButtonComponent text={t("auth.signIn")} loading={loading} />
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      </Box>
    </Container>
  );
};

export default SignIn;
